var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { SaveStatus } from 'lib-shared-legacy-types';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { selectQueueState } from 'app/utils/reselect';
import { useScheduleFetch } from 'components/powerFlowShared/hooks/useScheduleFetch';
import { useTagFetch } from 'components/powerFlowShared/hooks/useTagFetch';
import { FlowEditorChrome, FlowType } from 'fable/flowEditor';
import { withFlows, withTags, } from 'hocs';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { ElevioIntegrationProvider } from './elevioIntegrationProvider';
import styles from './flow.module.scss';
import { getNodesBasedOnFeatureFlags } from './getNodesBasedOnFeatureFlags';
import { useFlowData, useQueryElevioIntegration } from './hooks';
const FlowImpl = (props) => {
    const { tags, flows } = props;
    const { item: flow, getItem: getFlowItem } = flows;
    const notify = useNotifications();
    const { flowId } = useParams();
    const { queues } = useAppSelector(selectQueueState);
    const [saveStatus, setSaveStatus] = useState(SaveStatus.Saved);
    const [invalidNodes, setInvalidNodes] = useState(null);
    const { actions, active, messengerId, setActions, setActive } = useFlowData(getFlowItem, flowId);
    const elevioIntegration = useQueryElevioIntegration(messengerId);
    useTagFetch(tags, actions);
    const schedules = useScheduleFetch(actions);
    const featureFlags = useFeatureFlag([
        'integrations-for-dixa-messenger',
        'conversation-assistant-for-dixa-messenger',
        'chatbots-integration-settings',
    ]);
    const nodeTypes = getNodesBasedOnFeatureFlags(featureFlags);
    const hasUnsavedChanges = saveStatus <= SaveStatus.Unsaved;
    const handleOnChange = useCallback(({ actions: newActions, active: newActive, }) => {
        if (!hasUnsavedChanges ||
            actions !== newActions ||
            active !== newActive) {
            setSaveStatus(SaveStatus.Unsaved);
            setInvalidNodes(null);
            setActions(newActions);
            setActive(newActive);
        }
    }, [actions, active, hasUnsavedChanges, setActive, setActions]);
    const getCurrentFlow = useCallback((active) => {
        if (flow !== null && actions !== null) {
            return Object.assign(Object.assign({}, flow), { actions, active: flow.active || active });
        }
        return false;
    }, [actions, flow]);
    const handleOnActivate = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const updatedFlow = getCurrentFlow(true);
        if (updatedFlow && flowId) {
            yield flows.update(flowId, updatedFlow);
            setSaveStatus(SaveStatus.Saved);
            setActive(true);
        }
    }), [flowId, flows, getCurrentFlow, setActive]);
    const handleOnSave = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const updatedFlow = getCurrentFlow(false);
        if (updatedFlow && flowId) {
            try {
                yield flows.update(flowId, updatedFlow);
                setSaveStatus(SaveStatus.Saved);
                notify({
                    title: 'Saved',
                    text: `"${flow === null || flow === void 0 ? void 0 : flow.name}" is saved.`,
                    time: 3000,
                }, NotificationType.Success);
            }
            catch (err) {
                setSaveStatus(SaveStatus.Failed);
                notify({
                    title: 'Could not save',
                    text: `There was an issue when "${flow === null || flow === void 0 ? void 0 : flow.name}" was saved. ${err}`,
                }, NotificationType.Error);
            }
        }
    }), [flowId, flows, flow, getCurrentFlow, notify]);
    return (_jsx(ElevioIntegrationProvider, Object.assign({ elevioIntegration: elevioIntegration }, { children: _jsx("div", Object.assign({ className: styles.root }, { children: flow !== null && (_jsx(FlowEditorChrome, { disableTitleEdit: true, errorMessage: null, extraData: {
                    name: flow.name,
                    queues,
                    tags: tags.items,
                    schedules,
                }, flowType: FlowType.FLOW_TYPE_MESSENGER, goBackPath: '/settings/dixa-messenger', hasUnsavedChanges: hasUnsavedChanges, initialData: flow, invalidNodes: invalidNodes, nodeTypes: nodeTypes, onActivate: handleOnActivate, onChange: handleOnChange, onSave: handleOnSave })) })) })));
};
export const Flow = withTags(withFlows(FlowImpl));
